import router, { resetRouter } from "./index.js";
import store from "@/store/index.js";
import { toRouterConfig } from "./routerPermission.js";
import {
  composeRoutes,
  assembleMenuTree,
  firstChildPath,
  readBaseFrom,
  readOverlayFrom,
  writeOverlayTo,
  clearOverlayIn,
  isOverlayPath,
  reconstructOverlay,
  resolveScopedWeishopPath,
  shouldResetOverlayForPath as shouldResetOverlayForOverlayPath,
  shouldResetOverlayForNavigation as shouldResetOverlayForOverlayNavigation,
} from "./menuRuntime.core.js";
import { clearAuthSessionData, clearSnapshot, broadcastLogout } from "@/util/authSession.js";

function storage() { return window.sessionStorage; }

let applyingRoutes = false;

function isApplyingRoutes() {
  return applyingRoutes;
}

function debugEnabled() {
  try {
    return window.localStorage && window.localStorage.getItem("loginBootstrapDebug") === "1";
  } catch (e) {
    return false;
  }
}

function now() {
  return window.performance && window.performance.now ? window.performance.now() : Date.now();
}

function debugLog(event, payload) {
  if (debugEnabled()) console.log("[login-bootstrap]", event, payload || {});
}

function countNodes(nodes) {
  if (!Array.isArray(nodes)) return 0;
  return nodes.reduce((total, node) => total + 1 + countNodes(node.children || node._child || []), 0);
}

// 用持久化 overlay 重建 router + store。matcher 的唯一出口。
function applyRoutes() {
  const totalStart = now();
  const readStart = now();
  const base = readBaseFrom(storage());
  const overlay = readOverlayFrom(storage());
  const readCost = now() - readStart;
  const composeStart = now();
  const composed = composeRoutes(base, overlay);
  const composeCost = now() - composeStart;
  const assembleStart = now();
  const { tree } = assembleMenuTree(composed, 1);
  const assembleCost = now() - assembleStart;
  const configStart = now();
  const routerConfig = toRouterConfig(tree);
  const configCost = now() - configStart;
  const addRoutesStart = now();
  applyingRoutes = true;
  try {
    resetRouter();
    router.addRoutes(routerConfig);
  } finally {
    applyingRoutes = false;
  }
  const addRoutesCost = now() - addRoutesStart;
  const textStart = now();
  const routeText = JSON.stringify(tree);
  storage().setItem("routestext", routeText);
  const textCost = now() - textStart;
  const storeStart = now();
  store.commit("setMenuTree", tree);
  store.commit("setActivePlugin", overlay ? overlay.pluginId : 0);
  store.commit("pluginrouteadd", overlay ? overlay.pluginId : 0); // 兼容旧 state.pluginroute
  store.commit("routestatus", 1);
  const storeCost = now() - storeStart;
  debugLog("menuRuntime.applyRoutes", {
    baseTop: base.length,
    baseNodes: countNodes(base),
    treeTop: tree.length,
    treeNodes: countNodes(tree),
    overlay: overlay ? overlay.pluginId : 0,
    routeTextBytes: routeText.length,
    timings: {
      read: Math.round(readCost),
      compose: Math.round(composeCost),
      assemble: Math.round(assembleCost),
      toRouterConfig: Math.round(configCost),
      resetAddRoutes: Math.round(addRoutesCost),
      stringifyStoreRoutes: Math.round(textCost),
      storeCommits: Math.round(storeCost),
      total: Math.round(now() - totalStart),
    },
  });
}

// 进插件:页面只需传 pluginId + children(+ channel 类 match)。返回首子页用于 push。
function enterPlugin(opts) {
  const overlay = {
    pluginId: opts.pluginId,
    children: opts.children || [],
    match: opts.match || { is_plugin: 1 },
  };
  writeOverlayTo(storage(), overlay);
  applyRoutes();
  return firstChildPath(overlay.children);
}

// 返回落地页:清 overlay,base 自动还原。
function prepareLeavePlugin() {
  clearOverlayIn(storage());
  storage().setItem("pluginskipreconstruct", "1");
  store.commit("setActivePlugin", 0);
  store.commit("pluginrouteadd", 0);
  store.commit("routestatus", 0);
}

function leavePlugin() {
  prepareLeavePlugin();
  applyRoutes();
}

function shouldResetOverlayForPath(path) {
  return shouldResetOverlayForOverlayPath(readOverlayFrom(storage()), path);
}

function shouldResetOverlayForNavigation(toPath, fromPath) {
  return shouldResetOverlayForOverlayNavigation(readOverlayFrom(storage()), toPath, fromPath);
}

function resolveScopedPath(fromPath, toPath) {
  return resolveScopedWeishopPath(readBaseFrom(storage()), readOverlayFrom(storage()), fromPath, toPath);
}

// 守卫:首次/刷新/深链。base 已在 sessionStorage.routes;overlay 在持久键或从 base 重建。
function bootstrap(to) {
  const start = now();
  const path = to && to.path;
  let overlay = readOverlayFrom(storage());
  const skipReconstruct = storage().getItem("pluginskipreconstruct") === "1";
  debugLog("menuRuntime.bootstrap:start", {
    path,
    overlay: overlay ? overlay.pluginId : 0,
    skipReconstruct,
  });
  // 当前路径不在插件子树内(落地页/外部页) -> 不套 overlay
  if (overlay && !isOverlayPath(overlay, path)) {
    overlay = null;
    clearOverlayIn(storage());
  }
  // 无持久 overlay 但深链命中某插件子树 -> 从 base 重建(书签/刷新插件子页)
  if (!overlay && path && !skipReconstruct) {
    const recon = reconstructOverlay(readBaseFrom(storage()), path);
    if (recon) writeOverlayTo(storage(), recon);
  }
  applyRoutes();
  if (skipReconstruct) storage().removeItem("pluginskipreconstruct");
  debugLog("menuRuntime.bootstrap:end", {
    path,
    elapsed: Math.round(now() - start),
  });
}

// 源刷新(如 plugin_detail.refreshShopMenu 拉到新整菜单)
function setBase(routes) {
  storage().setItem("routes", JSON.stringify(routes));
  clearOverlayIn(storage());
  applyRoutes();
}

// 登出/禁用
function reset() {
  const session = storage();
  const loginPage = session.getItem("loginPage");
  const logoutScope = {
    accountScope: session.getItem("accountScope") || "",
    loginPage: loginPage || "",
    guardName: session.getItem("guardName") || "",
  };
  clearOverlayIn(session);
  clearAuthSessionData(session);
  if (loginPage) session.setItem("loginPage", loginPage);
  store.commit("setMenuTree", []);
  store.commit("setActivePlugin", 0);
  store.commit("pluginrouteadd", 0);
  store.commit("routestatus", 0);
  resetRouter();
  clearSnapshot();   // 清跨窗口会话快照（本地）
  broadcastLogout(logoutScope); // 只通知同账号类型窗口登出，避免区域账号误踢店铺账号
}

export default { applyRoutes, enterPlugin, prepareLeavePlugin, leavePlugin, shouldResetOverlayForPath, shouldResetOverlayForNavigation, resolveScopedPath, isApplyingRoutes, bootstrap, setBase, reset };
export { applyRoutes, enterPlugin, prepareLeavePlugin, leavePlugin, shouldResetOverlayForPath, shouldResetOverlayForNavigation, resolveScopedPath, isApplyingRoutes, bootstrap, setBase, reset };
